import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { BusinessScope } from "@lib/constants/business-scope";

import { TemplateDto } from "@domain/project/template/index";
import { ManifestService } from "@domain/project/template/mainfest.service";
import { plainToInstance } from "class-transformer";
import { validateSync } from "class-validator";
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap, throwError } from "rxjs";

@Injectable({ providedIn: "root" })
export class TemplateService {
  private http: HttpClient = inject(HttpClient);
  private manifestService = inject(ManifestService);
  private readonly TEMPLATES_DIR = "assets/templates";
  private readonly FGDS_COUNTRY_TEMPLATES_DIR = "assets/templates/fgds-country-templates";
  private readonly GLOBAL_FGDS_TEMPLATE_NAME = "fgds-global.json" as const;
  private readonly GLOBAL_MONITORING_TEMPLATE_NAME = "monitoring-global.json" as const;

  private _projectTemplate$ = new BehaviorSubject<TemplateDto | undefined>(undefined);

  get projectTemplate$(): Observable<TemplateDto | undefined | null> {
    return this._projectTemplate$.asObservable();
  }

  setProjectTemplate(template: TemplateDto | null): void {
    this._projectTemplate$.next(template ?? undefined);
  }

  generateTemplate(businessScope: BusinessScope, country: string) {
    return this.mapTemplatePaths(businessScope, country).pipe(
      switchMap(({ globalPath, countryPath }) => {
        const scopeTemplate$ = this.loadTemplateData(globalPath);
        const countryTemplate$ = countryPath ? this.loadTemplateData(countryPath) : of(null);

        return forkJoin({
          scopeTemplate: scopeTemplate$,
          countryTemplate: countryTemplate$,
        });
      }),
      map(({ scopeTemplate, countryTemplate }) => {
        this.validateTemplate(scopeTemplate);
        if (!countryTemplate) return scopeTemplate;

        const mergedTemplate = this.deepMerge({ ...scopeTemplate }, countryTemplate);
        this.validateTemplate(mergedTemplate);
        return mergedTemplate;
      }),
    );
  }

  getGlobalTemplate(businessScope: BusinessScope) {
    return this.mapTemplatePaths(businessScope).pipe(
      switchMap((globalPath: { globalPath: string }) => {
        return this.loadTemplateData(globalPath.globalPath);
      }),
      map((scopeTemplate) => {
        this.validateTemplate(scopeTemplate);
        return scopeTemplate;
      }),
    );
  }

  private mapTemplatePaths(
    businessScope: BusinessScope,
    country?: string,
  ): Observable<{ globalPath: string; countryPath?: string }> {
    switch (businessScope) {
      case BusinessScope.FGDS: {
        const globalPath = `${this.TEMPLATES_DIR}/${this.GLOBAL_FGDS_TEMPLATE_NAME}`;

        if (!country) {
          return of({ globalPath });
        }
        const countryPath = `${this.FGDS_COUNTRY_TEMPLATES_DIR}/${country.toLowerCase()}-fgds.json`;
        return this.manifestService.getTemplatePaths().pipe(
          map((templatePaths) => {
            if (templatePaths.some((path) => countryPath.includes(path))) {
              return { globalPath, countryPath };
            }
            return { globalPath };
          }),
        );
      }
      case BusinessScope.MONITORING: {
        const globalPath = `${this.TEMPLATES_DIR}/${this.GLOBAL_MONITORING_TEMPLATE_NAME}`;
        return of({ globalPath });
      }
      default:
        return throwError(() => new Error(`No template mapping defined for scope: ${businessScope}`));
    }
  }

  private loadTemplateData(url: string): Observable<TemplateDto | undefined> {
    return this.http.get<TemplateDto>(url).pipe(
      catchError((err) => {
        if (err.status === 404) {
          return of(undefined);
        }
        throw err;
      }),
    );
  }

  private validateTemplate(templateData: unknown) {
    if (!templateData) {
      throw new Error("Template data is empty or undefined");
    }
    const template = plainToInstance(TemplateDto, templateData);
    const errors = validateSync(template, { whitelist: true });
    if (errors.length > 0) {
      throw new Error(`Invalid template JSON: ${JSON.stringify(errors)}`);
    }
    return template;
  }

  private deepMerge<T>(target: T, source: Partial<T>): T {
    if (!source || typeof source !== "object") return target;

    const sourceRecord = source as Record<string, unknown>;
    const targetRecord = target as unknown as Record<string, unknown>;

    for (const key of Object.keys(source)) {
      const value = sourceRecord[key];
      const targetValue = targetRecord[key];

      const bothArePlainObjects =
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        targetValue &&
        typeof targetValue === "object" &&
        !Array.isArray(targetValue);

      try {
        if (bothArePlainObjects) {
          // deep merge recursively
          targetRecord[key] = this.deepMerge(
            { ...(targetValue as Record<string, unknown>) },
            value as Partial<Record<string, unknown>>,
          );
        } else if (value !== undefined) {
          // overwrite with source value
          targetRecord[key] = value;
        }
      } catch (err) {
        console.error(`[deepMerge] Failed to merge key "${key}"`, { targetValue, value, err });
      }
    }

    return target;
  }
}
